export type Tier = 1 | 2 | 3;
export type LeadStatus = 'new' | 'contacted' | 'follow_up' | 'qualified' | 'won' | 'lost';
export type AssignedFilter = '' | 'me' | 'unassigned' | 'all';

export const STATUSES: LeadStatus[] = ['new', 'contacted', 'follow_up', 'qualified', 'won', 'lost'];

export const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  follow_up: 'Follow Up',
  qualified: 'Qualified',
  won: 'Won',
  lost: 'Lost',
};

export const STATUS_COLOR: Record<LeadStatus, string> = {
  new: 'bg-slate-200 text-slate-800',
  contacted: 'bg-blue-100 text-blue-800',
  follow_up: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-purple-100 text-purple-800',
  won: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-rose-100 text-rose-800',
};

export type Lead = {
  id: string;
  name: string;
  phone: string;
  raw_answers: string | null;
  email?: string | null;
  created_at: string;
  tier_score: Tier;
  persuasion_tip: string;
  opening_line: string;
  status: LeadStatus;
  assigned_to: string | null;
  last_assigned_to?: string | null;
  last_assigned_at?: string | null;
  updated_at: string;
  source: 'whatsapp' | 'meta_form' | 'call' | 'neodove';
  whatsapp_number_used?: string;
  clinic_tag?: string;
  tags?: string[];
};

export type LeadEvent = {
  id: string;
  kind: string;
  actor: string | null;
  data: string | null;
  created_at: string;
};

export type CallLog = {
  id: string;
  duration: number;
  recording_url: string | null;
  initiated_by: string | null;
  created_at: string;
};

export type Telecaller = {
  email: string;
  name: string | null;
  phone: string | null;
  active: number;
  last_seen: string | null;
};

export type Me = {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  role: 'super_admin' | 'admin' | 'manager' | 'telecaller';
  active: number;
  created_at?: string;
  last_seen?: string | null;
};

export type Insights = {
  scope: 'team' | 'me';
  totals: { total_leads: number; total_calls: number; total_seconds: number; unassigned: number };
  today: { new_leads: number; calls_made: number; wins_today: number };
  by_status: Record<string, number>;
  by_tier: Record<string, number>;
  by_source: Record<string, number>;
  by_campaign: Record<string, number>;
  conversion_rate: number;
  top_telecallers: { assigned_to: string; n: number }[];
};

export type Tab = 'unassigned' | 'queue' | 'leads' | 'insights';

export type IvrOption = {
  digit: string;
  label: string;
  topic: string;
  tier: 1 | 2 | 3;
  tip: string;
  opening_line: string;
  auto_wa_template?: string;
};

export type IvrConfig = {
  campaign: string;
  display_name: string | null;
  greeting: string;
  greeting_audio_url: string | null;
  thank_you_audio_url: string | null;
  options_json: string;
  no_input_tier: number;
  no_input_topic: string;
  no_input_tip: string | null;
  no_input_opening: string | null;
  voice: 'woman' | 'man';
  active: number;
  business_hours_start?: string;
  business_hours_end?: string;
  business_days?: string;
  timezone?: string;
  after_hours_greeting?: string | null;
  after_hours_greeting_audio_url?: string | null;
  after_hours_options_json?: string;
  support_phone?: string | null;
  created_at: string;
  updated_at: string;
};

export type MetaPage = {
  page_id: string;
  name: string | null;
  active: number;
  created_at: string;
  updated_at: string;
  last_used: string | null;
  token_preview: string;
};

export type WaTemplate = {
  name: string;
  label: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: 'APPROVED' | 'PENDING' | 'REJECTED';
  language: string;
  params: string[];
  has_button?: boolean;
  has_media?: 'video' | 'image' | 'document';
  note?: string;
};

export type Followup = {
  id: string;
  lead_id: string;
  followup_date: string;
  followup_time: string | null;
  notes: string | null;
  status: 'pending' | 'completed' | 'cancelled';
  created_by: string;
  created_at: string;
  completed_at: string | null;
};
